import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, ShieldCheck, CreditCard, ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { EventDropNav } from '../components/EventDropLogo';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Mode 1 — Atmospheric Editorial tokens ───
// Shared with BeginPage / CreateEventPage / LoginPage so the funnel
// reads as a single emotional surface.
const PAPER_BG  = '#1c1108';
const PAPER_HI  = '#2a190a';
const INK       = '#FFE7A8';
const INK_MUTED = 'rgba(255,235,210,0.62)';
const INK_FAINT = 'rgba(255,235,210,0.38)';
const GOLD      = '#D4AF5E';
const HAIR      = 'rgba(212,170,82,0.32)';
const SERIF     = 'Georgia, "Times New Roman", serif';

// Canonical 3-tier lineup (May 2026) — Essentials / Experience / Premium.
// Slugs + prices + products MUST match backend `CONVERSION_PACKAGES` in
// server.py and the matching block in PreCheckout.js. Old slugs (capture,
// legacy, snap, story) are retained as back-compat ALIASES for in-flight
// Stripe redirects and shared old links — they resolve to the price the
// user originally saw, never to the new canonical tier.
const PACKAGES = {
  // ── iter194 — Editorial Editions (canonical, constitutional) ──
  // These mirror EDITIONS_CATALOGUE in /app/backend/server.py.
  // The /create funnel now routes the homepage's authored CTAs into
  // these (Commission The Keepsake/Room/Archive ✦ · Book Leavers
  // Night ✦), bypassing the legacy add-on funnel entirely.
  keepsake: {
    name: 'The Keepsake ✦', price: 30,
    tagline: 'A quiet shared memory space.',
    products: ['highlight'],
    features: ['QR uploads', 'Memory Snap', 'Memory Wall',
               'Recap Letters', 'Archive Shelf', 'Highlight Reel'],
    edition: true,
  },
  room: {
    name: 'The Room ✦', price: 60,
    tagline: 'A live participation experience for the entire room.',
    products: ['highlight', 'booth_os', 'pocket_booth', 'guestbook',
               'challenges', 'invites'],
    features: ['Booth OS', 'Pocket Booth', 'Video & audio Guestbook',
               'Mega Missions', 'Invites & RSVP', 'Highlight Reel',
               'Memory Wall', 'Memory Snap'],
    edition: true,
  },
  archive: {
    name: 'The Archive ✦', price: 99,
    tagline: 'A cinematic preservation system for the night.',
    products: ['highlight', 'booth_os', 'pocket_booth', 'guestbook',
               'challenges', 'invites',
               'spin_360', 'film', 'memory_book', 'lens_notes', 'pro_upgrade'],
    features: ['Full Booth OS', 'Pocket Booth', 'Video & audio Guestbook',
               'Mega Missions', 'Invites & RSVP', 'Memory Wall',
               'Spin Film 360', 'Event Film', 'Highlight Reel',
               'Memory Guestbook', 'Lens Notes ✦'],
    edition: true,
  },
  leavers_night: {
    name: 'Leavers Night ✦', price: 99,
    tagline: 'A live participation experience for the year group.',
    products: ['booth_os', 'pocket_booth', 'guestbook',
               'challenges', 'invites'],
    features: ['Booth OS', 'Remember Us ✦', 'Pocket Booth', 'Memory Snap',
               'Lens Notes', 'Drop One Word', 'Live projector wall',
               'Shared atmosphere system'],
    edition: true,
  },
  // ── Legacy non-editorial SKUs — retained for old paid links ──
  essentials: {
    name: 'Essentials', price: 39,
    tagline: 'Every moment. One place.',
    products: ['capture', 'invites', 'highlight'],
    features: ['Unlimited guest uploads', 'Private event gallery', 'QR code access', 'Personalised invites', 'Highlight Reel', 'Full gallery download'],
  },
  experience: {
    name: 'Experience', price: 145, popular: true,
    tagline: 'Memories + atmosphere',
    products: ['capture', 'invites', 'highlight', 'memory_book',
               'guestbook', 'pocket_booth', 'challenges'],
    features: ['Everything in Essentials', 'Memory Guestbook', 'Video Guestbook', 'Pocket Booth', 'Challenge Mode'],
  },
  premium: {
    name: 'Premium', price: 245,
    tagline: 'The complete event experience',
    products: ['capture', 'invites', 'highlight', 'memory_book',
               'guestbook', 'pocket_booth', 'spin_360', 'challenges',
               'film', 'pro_upgrade'],
    features: ['Everything in Experience', 'Spin Film 360', 'Full Event Film', 'Pro Upgrade (no watermark + premium music)'],
  },
  // ── Legacy SKUs — retained for old links, NEVER shown on selector ──
  capture: {
    name: 'The Capture (legacy)', price: 39,
    tagline: 'Legacy package', products: ['capture', 'highlight'],
    features: ['Unlimited guest uploads', 'Private event gallery', 'Highlight Reel'],
  },
  legacy: {
    name: 'The Legacy (legacy)', price: 249,
    tagline: 'Legacy package',
    products: ['capture', 'pocket_booth', 'guestbook', 'challenges',
               'highlight', 'film', 'booth', 'invites',
               'pro_upgrade', 'memory_book'],
    features: ['Everything in Experience', 'Photobooth', 'Full Event Film', 'Pro Upgrade'],
  },
  snap: {
    name: 'The Snap (legacy)', price: 49,
    tagline: 'Legacy package', products: ['capture'], features: ['QR code guest uploads'],
  },
  story: {
    name: 'The Story (legacy)', price: 99,
    tagline: 'Legacy package', products: ['capture', 'guestbook', 'highlight'], features: ['QR guest uploads'],
  },
};

const CARD_TIERS = ['essentials', 'experience', 'premium'];

const PAGE_BG = `radial-gradient(1100px 700px at 50% -100px, ${PAPER_HI} 0%, ${PAPER_BG} 60%, #140a04 100%)`;

const EventCheckout = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const urlPackage = searchParams.get('package');
  const urlEventName = searchParams.get('event_name') || '';
  const urlEventDate = searchParams.get('event_date') || '';
  const urlPromo = searchParams.get('promo') || '';
  const validUrlPackage = urlPackage && PACKAGES[urlPackage] ? urlPackage : null;
  const initialStep = validUrlPackage ? 2 : 1;
  const [step, setStep] = useState(initialStep);
  const [selectedPkg, setSelectedPkg] = useState(validUrlPackage || 'experience');
  const [eventName, setEventName] = useState(urlEventName);
  const [eventDate, setEventDate] = useState(urlEventDate);
  // iter215 — account creation moved INTO checkout. The host's email
  // + password is captured on step 2 and an account is created (or
  // signed-in if the email already has one) before Stripe is ever
  // touched. This guarantees the account exists even if the host
  // drops off after payment, so the booking is never orphaned.
  const [email, setEmail] = useState(auth?.user?.email || '');
  const [password, setPassword] = useState('');
  // 'register' (default) or 'login' — flipped when /auth/register
  // returns "account already exists" so the host can sign in with the
  // same form rather than starting over.
  const [authMode, setAuthMode] = useState('register');
  const [authBusy, setAuthBusy] = useState(false);
  const [authReady, setAuthReady] = useState(Boolean(auth?.user));
  const [authNotice, setAuthNotice] = useState('');
  const [promoCode, setPromoCode] = useState(urlPromo);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [promoApplied, setPromoApplied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [founderStatus, setFounderStatus] = useState(null);

  // Already-signed-in hosts skip the account creation step entirely —
  // their session token will auto-attach to /funnel/checkout via the
  // axios interceptor, so we just move on.
  useEffect(() => {
    if (auth?.user && !authReady) {
      setAuthReady(true);
      setEmail(auth.user.email || '');
    }
  }, [auth?.user, authReady]);

  useEffect(() => {
    fetch(`${API}/pricing/legacy-founder`).then(r => r.json()).then(setFounderStatus).catch(() => {});
  }, []);

  // Pre-apply the promo carried in from the commission flow so the
  // discount survives the handoff. The validation request is the
  // same the manual flow uses — if it fails, the field stays empty
  // and the user can re-type or move on.
  useEffect(() => {
    if (!urlPromo) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/promo/validate`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: urlPromo }),
        });
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.valid) {
          setPromoDiscount(data.discount || 0);
          setPromoApplied(true);
        }
      } catch { /* silent — user can re-enter manually */ }
    })();
    return () => { cancelled = true; };
  }, [urlPromo]);

  const pkg = PACKAGES[selectedPkg] || PACKAGES.experience;
  const isFounderLegacy = selectedPkg === 'legacy' && founderStatus?.active;
  const effectivePrice = isFounderLegacy ? founderStatus.founder_price : pkg.price;
  const finalPrice = Math.max(0, Math.round(effectivePrice * (1 - promoDiscount / 100) * 100) / 100);

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    try {
      const res = await fetch(`${API}/promo/validate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: promoCode.trim() })
      });
      const data = await res.json();
      if (data.valid) {
        setPromoDiscount(data.discount || 0);
        setPromoApplied(true);
      } else {
        setError('Invalid promo code');
      }
    } catch { setError('Could not validate promo'); }
  };

  // iter215 — register the host (or sign in if the email already
  // exists) right inside step 2. We hold the host on the same form
  // and surface a single soft notice when we flip to login mode, so
  // the entered event details stay intact across the mode switch.
  // The session token is stored via the AuthContext.login helper so
  // the axios interceptor automatically attaches Bearer auth to the
  // subsequent /funnel/checkout call — the booking lands attributed
  // to the account before Stripe is ever touched.
  const ensureAccount = async () => {
    setError('');
    setAuthNotice('');
    if (auth?.user) {
      setAuthReady(true);
      return true;
    }
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !password) {
      setError('An email and password are needed so you can return.');
      return false;
    }
    if (password.length < 6) {
      setError('Your password needs at least six characters.');
      return false;
    }
    setAuthBusy(true);
    try {
      if (authMode === 'register') {
        const res = await fetch(`${API}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            email: trimmedEmail,
            password,
            // The host's display name is the event name they typed —
            // they can change it later from the desk. Keeps the form
            // restrained: no extra field, no extra friction.
            name: eventName.trim() || trimmedEmail.split('@')[0],
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.session_token) {
          auth?.login?.(data.user, data.session_token);
          setAuthReady(true);
          setAuthBusy(false);
          return true;
        }
        if (res.status === 400 && /already exists/i.test(data?.detail || '')) {
          // Email already has an account — flip the same form into
          // sign-in mode, preserve every field, and let the host try
          // their existing password.
          setAuthMode('login');
          setAuthNotice('This email already has an account. Sign in to continue.');
          setAuthBusy(false);
          return false;
        }
        setError(data?.detail || 'Account creation failed. Please try again.');
        setAuthBusy(false);
        return false;
      }
      // login mode
      const res = await fetch(`${API}/auth/login-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: trimmedEmail, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.session_token) {
        auth?.login?.(data.user, data.session_token);
        setAuthReady(true);
        setAuthBusy(false);
        return true;
      }
      setError(data?.detail || 'Sign-in failed. Please check your details.');
      setAuthBusy(false);
      return false;
    } catch (e) {
      setError('Something went wrong. Please try again.');
      setAuthBusy(false);
      return false;
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    setError('');
    try {
      const endpoint = finalPrice === 0 ? `${API}/funnel/checkout-free` : `${API}/funnel/checkout`;
      const body = {
        products: pkg.products,
        origin_url: window.location.origin,
        email,
        package: selectedPkg,
        event_name: eventName,
        event_date: eventDate,
      };
      // Always carry the promo through — partial codes (TRY50 -> 50%)
      // need the backend to reduce the Stripe amount; 100% codes need
      // the backend to skip Stripe entirely. The endpoint switch above
      // is the only difference between the two paths.
      if (promoApplied && promoCode) {
        body.promo_code = promoCode.trim().toUpperCase();
      }
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // iter215 — attach the session token created by ensureAccount()
          // so /funnel/checkout stamps user_id onto the event + tx.
          ...(localStorage.getItem('session_token')
            ? { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
            : {}),
        },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        // Surface the backend's reason (e.g. "Invalid promo code",
        // "Promo code has reached its usage limit") so the user can act.
        setError(data?.detail || 'Checkout failed. Please try again.');
        setLoading(false);
        return;
      }
      // Constitutional checkout gate (Craig's 2026-05-13 evening lock):
      // every package CTA must flow Event Details → Selected Package
      // → Stripe Checkout → /preserved/:slug return. The previous
      // "free → /dashboard" path was a bypass that granted operational
      // access before Stripe confirmation; it is removed.
      if (data.url) {
        if (data.url.startsWith('http')) {
          window.location.href = data.url;
        } else {
          navigate(data.url);
        }
      } else if (data.session_id && data.slug) {
        navigate(`/preserved/${data.slug}?session_id=${data.session_id}`);
      } else if (data.session_id) {
        navigate(`/upsell?session_id=${data.session_id}`);
      } else {
        setError('Checkout failed. Please try again.');
      }
    } catch (e) {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  // ───────── STEP 1 — Package select ─────────
  if (step === 1) {
    return (
      <PageShell data-testid="checkout-step-1">
        <TopBar onBack={() => navigate('/event')}>
          <StepDots current={1} />
        </TopBar>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: 'clamp(28px, 6vw, 60px) 22px 80px' }}>
          <SectionLabel center>✦ CHOOSE A PACKAGE</SectionLabel>
          <SectionHeading center>Choose your package</SectionHeading>
          <SectionLede center>One payment. No subscriptions. Full refund guarantee.</SectionLede>

          {founderStatus?.active && (
            <div
              data-testid="founder-banner"
              style={{
                margin: '0 auto 28px', maxWidth: 560,
                borderRadius: 14, padding: '14px 18px',
                background: 'rgba(212,170,82,0.10)',
                border: `1px solid ${HAIR}`,
                display: 'flex', alignItems: 'center', gap: 14,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  fontFamily: SERIF, fontSize: 10.5, fontWeight: 800,
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                  color: GOLD, margin: 0,
                }}>Founder's price · launch offer</p>
                <p style={{
                  fontFamily: SERIF, fontSize: 13.5, color: INK,
                  margin: '4px 0 0', lineHeight: 1.45,
                }}>
                  The Legacy for <strong style={{ color: GOLD }}>£{founderStatus.founder_price}</strong>{' '}
                  <span style={{ textDecoration: 'line-through', color: INK_FAINT, fontSize: 12 }}>£{founderStatus.standard_price}</span>{' '}
                  — only <strong style={{ color: GOLD }}>{founderStatus.seats_remaining}</strong> of {founderStatus.seats_total} seats left.
                </p>
              </div>
            </div>
          )}

          <div style={{
            display: 'grid', gap: 14,
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          }}>
            {CARD_TIERS.map((key) => {
              const p = PACKAGES[key];
              const isSelected = key === selectedPkg;
              const isPopular = p.popular;
              const thisIsFounderLegacy = key === 'legacy' && founderStatus?.active;
              const cardPrice = thisIsFounderLegacy ? founderStatus.founder_price : p.price;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedPkg(key)}
                  data-testid={`select-${key}`}
                  style={{
                    position: 'relative', textAlign: 'left',
                    borderRadius: 16, padding: '22px 20px',
                    background: 'rgba(20,12,6,0.72)',
                    border: `1px solid ${isSelected ? GOLD : HAIR}`,
                    boxShadow: isSelected
                      ? '0 18px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(212,170,82,0.18)'
                      : '0 12px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(212,170,82,0.06)',
                    cursor: 'pointer',
                    transition: 'border-color 220ms ease, box-shadow 220ms ease',
                  }}
                >
                  {isPopular && (
                    <div style={{
                      position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                      padding: '3px 12px', borderRadius: 999,
                      fontFamily: SERIF, fontSize: 10, fontWeight: 800,
                      letterSpacing: '0.22em', textTransform: 'uppercase',
                      background: GOLD, color: '#0F0B1A',
                    }}>Most chosen</div>
                  )}
                  <h3 style={{
                    fontFamily: SERIF, fontWeight: 600,
                    fontSize: 18, color: INK, margin: 0,
                    letterSpacing: '0.01em',
                  }}>{p.name}</h3>
                  <p style={{
                    fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 12, color: INK_FAINT, margin: '4px 0 14px',
                    letterSpacing: '0.02em',
                  }}>{p.tagline}</p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    <span style={{
                      fontFamily: SERIF, fontWeight: 600,
                      fontSize: 32, color: INK, letterSpacing: '-0.01em',
                    }}>£{cardPrice}</span>
                    {thisIsFounderLegacy && (
                      <span style={{ fontFamily: SERIF, fontSize: 12, color: INK_FAINT, textDecoration: 'line-through' }}>£{p.price}</span>
                    )}
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {p.features.map((f, i) => (
                      <li key={i} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8,
                        fontFamily: SERIF, fontSize: 13, color: INK_MUTED,
                        letterSpacing: '0.01em', lineHeight: 1.45,
                      }}>
                        <Check size={12} weight="bold" color={GOLD} style={{ marginTop: 4, flexShrink: 0 }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {isSelected && (
                    <div style={{
                      position: 'absolute', top: 14, right: 14,
                      width: 22, height: 22, borderRadius: '50%',
                      background: GOLD,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Check size={12} weight="bold" color="#1c1108" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          <div style={{ textAlign: 'center', marginTop: 36 }}>
            <button
              onClick={() => setStep(2)}
              data-testid="checkout-next-1"
              style={ctaButtonStyle()}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Continue <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  // ───────── STEP 2 — Event details + account ─────────
  if (step === 2) {
    // iter215 — host must commit an email + password (or sign in)
    // BEFORE the order summary so the booking can never be orphaned
    // by a Stripe drop-off. Already-signed-in hosts skip the password
    // field entirely.
    const accountOk = Boolean(auth?.user) || (email.trim() && password.length >= 6);
    const canProceed = eventName.trim() && eventDate && accountOk;
    return (
      <PageShell data-testid="checkout-step-2">
        <TopBar onBack={() => (validUrlPackage ? navigate('/') : setStep(1))}>
          <StepDots current={2} />
        </TopBar>
        <div style={{ maxWidth: 480, margin: '0 auto', padding: 'clamp(28px, 6vw, 60px) 22px 80px' }}>
          <SectionLabel center>✦ THE NIGHT'S DETAILS</SectionLabel>
          <SectionHeading center>Your event details</SectionHeading>
          <SectionLede center>We'll set everything up for you.</SectionLede>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field label="Event name">
              <input
                type="text"
                value={eventName}
                onChange={e => setEventName(e.target.value)}
                placeholder="e.g. Sarah & Tom's Wedding"
                data-testid="input-event-name"
                style={inputBase}
              />
            </Field>
            <Field label="Event date">
              <input
                type="date"
                value={eventDate}
                onChange={e => setEventDate(e.target.value)}
                data-testid="input-event-date"
                style={{ ...inputBase, colorScheme: 'dark' }}
              />
            </Field>
            <Field label="Your email">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="input-email"
                disabled={Boolean(auth?.user)}
                style={{
                  ...inputBase,
                  ...(auth?.user ? { opacity: 0.65, cursor: 'not-allowed' } : {}),
                }}
              />
            </Field>
            {!auth?.user && (
              <Field
                label={
                  authMode === 'login'
                    ? 'Your password'
                    : 'Set a password'
                }
              >
                <input
                  type="password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  placeholder={authMode === 'login' ? 'Your password' : 'At least six characters'}
                  data-testid="input-password"
                  autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                  style={inputBase}
                />
                <p style={{
                  margin: '8px 0 0',
                  fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 11.5, color: INK_FAINT, lineHeight: 1.5,
                  letterSpacing: '0.02em',
                }}>
                  {authMode === 'login'
                    ? 'Sign in so this night joins your existing collection.'
                    : 'So you can return to the night whenever you like.'}
                </p>
              </Field>
            )}
            {authNotice && (
              <p
                data-testid="auth-mode-notice"
                style={{
                  margin: 0,
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: 'rgba(212,170,82,0.10)',
                  border: `1px solid ${HAIR}`,
                  fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 12.5, color: GOLD, letterSpacing: '0.02em',
                }}
              >
                {authNotice}
              </p>
            )}
            {error && (
              <p
                data-testid="account-error"
                style={{
                  margin: 0,
                  fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 12.5, color: '#FFAA90',
                }}
              >
                {error}
              </p>
            )}
          </div>

          <button
            onClick={async () => {
              if (!canProceed) return;
              const ok = await ensureAccount();
              if (ok) setStep(3);
            }}
            disabled={!canProceed || authBusy}
            data-testid="checkout-next-2"
            style={ctaButtonStyle(Boolean(canProceed) && !authBusy, { width: '100%', marginTop: 30 })}
            onMouseEnter={(e) => { if (canProceed && !authBusy) e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            {authBusy
              ? 'Preparing your account…'
              : auth?.user
                ? <>Review order <ArrowRight size={16} weight="bold" /></>
                : authMode === 'login'
                  ? <>Sign in & review order <ArrowRight size={16} weight="bold" /></>
                  : <>Create account & review order <ArrowRight size={16} weight="bold" /></>}
          </button>
        </div>
      </PageShell>
    );
  }

  // ───────── STEP 3 — Summary + payment ─────────
  return (
    <PageShell data-testid="checkout-step-3">
      <TopBar onBack={() => setStep(2)}>
        <StepDots current={3} />
      </TopBar>
      <div style={{ maxWidth: 480, margin: '0 auto', padding: 'clamp(28px, 6vw, 60px) 22px 80px' }}>
        <SectionLabel center>✦ ORDER SUMMARY</SectionLabel>
        <SectionHeading center>Order summary</SectionHeading>

        <div
          style={{
            borderRadius: 14, padding: '20px 20px',
            background: 'rgba(20,12,6,0.72)',
            border: `1px solid ${HAIR}`,
            marginTop: 18, marginBottom: 16,
            boxShadow: '0 12px 40px rgba(0,0,0,0.35), inset 0 1px 0 rgba(212,170,82,0.06)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <h3 style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 17, color: INK, margin: 0, letterSpacing: '0.01em' }}>{pkg.name}</h3>
              <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, color: INK_FAINT, margin: '4px 0 0' }}>
                {eventName} · {eventDate}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 24, color: INK, letterSpacing: '-0.01em' }}>
                £{isFounderLegacy ? founderStatus.founder_price : pkg.price}
              </span>
              {isFounderLegacy && (
                <span style={{ fontFamily: SERIF, fontSize: 11, color: INK_FAINT, textDecoration: 'line-through', marginLeft: 6 }}>£{pkg.price}</span>
              )}
            </div>
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pkg.features.map((f, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: SERIF, fontSize: 12.5, color: INK_MUTED }}>
                <Check size={11} weight="bold" color={GOLD} /> {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Promo */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input
            type="text"
            value={promoCode}
            onChange={e => { setPromoCode(e.target.value); setPromoApplied(false); setError(''); }}
            placeholder="Promo code"
            data-testid="input-promo"
            style={{
              flex: 1, padding: '11px 14px',
              borderRadius: 12,
              background: 'rgba(20,12,6,0.78)',
              border: `1px solid ${HAIR}`,
              color: INK, fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 14, outline: 'none',
              boxShadow: 'inset 0 1px 0 rgba(212,170,82,0.08)',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = GOLD; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = HAIR; }}
          />
          <button
            onClick={applyPromo}
            data-testid="apply-promo"
            style={{
              padding: '11px 18px', borderRadius: 12,
              fontFamily: SERIF, fontWeight: 600, fontSize: 12.5,
              letterSpacing: '0.04em',
              background: 'transparent',
              color: INK, border: `1px solid ${HAIR}`,
              cursor: 'pointer',
              transition: 'background 220ms ease, border-color 220ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(212,170,82,0.08)'; e.currentTarget.style.borderColor = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = HAIR; }}
          >Apply</button>
        </div>
        {promoApplied && (
          <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, color: GOLD, margin: '0 0 10px' }}>
            {promoDiscount}% discount applied.
          </p>
        )}

        {/* Total */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 0', borderTop: `1px solid ${HAIR}`, marginBottom: 6,
        }}>
          <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 13, color: INK_MUTED, letterSpacing: '0.06em' }}>Total</span>
          <div style={{ textAlign: 'right' }}>
            {promoApplied && (
              <span style={{ fontFamily: SERIF, fontSize: 12, color: INK_FAINT, textDecoration: 'line-through', marginRight: 8 }}>
                £{isFounderLegacy ? founderStatus.founder_price : pkg.price}
              </span>
            )}
            <span style={{ fontFamily: SERIF, fontWeight: 600, fontSize: 28, color: INK, letterSpacing: '-0.01em' }}>£{finalPrice}</span>
          </div>
        </div>

        {/* Trust */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 14,
          justifyContent: 'center', marginBottom: 22, marginTop: 8,
        }}>
          <Trust icon={<ShieldCheck size={12} weight="bold" />} text="Secure payment" />
          <Trust icon={<Check size={12} weight="bold" />} text="Instant setup" />
          <Trust icon={<Check size={12} weight="bold" />} text="Full refund guarantee" />
        </div>

        {error && (
          <p
            data-testid="checkout-error"
            style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5, color: '#FFAA90', textAlign: 'center', margin: '0 0 12px' }}
          >{error}</p>
        )}

        <button
          onClick={handleCheckout}
          disabled={loading}
          data-testid="checkout-pay"
          style={ctaButtonStyle(!loading, { width: '100%' })}
          onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          {loading ? (
            <span
              style={{
                width: 18, height: 18, borderRadius: '50%',
                border: `2px solid rgba(15,11,26,0.3)`,
                borderTopColor: '#0F0B1A',
                animation: 'editorialSpin 700ms linear infinite',
              }}
            />
          ) : (
            <>
              {finalPrice === 0 ? 'Activate' : `Pay £${finalPrice}`}{' '}
              <CreditCard size={16} weight="bold" />
            </>
          )}
        </button>

        <p style={{
          textAlign: 'center', fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11.5, color: INK_FAINT, letterSpacing: '0.04em',
          margin: '14px 0 0',
        }}>
          Powered by Stripe. Apple Pay &amp; Google Pay accepted.
        </p>
      </div>
    </PageShell>
  );
};

/* ─── shared editorial primitives ─── */

const PageShell = ({ children, ...rest }) => (
  <div
    style={{ minHeight: '100vh', background: PAGE_BG, color: INK, fontFamily: SERIF }}
    {...rest}
  >
    {children}
    <style>{`
      @keyframes editorialSpin { to { transform: rotate(360deg); } }
      input::placeholder { color: rgba(255,235,210,0.32); }
      input::-webkit-calendar-picker-indicator { filter: invert(0.7) sepia(0.6) saturate(2) hue-rotate(2deg); cursor: pointer; }
    `}</style>
  </div>
);

const TopBar = ({ onBack, children }) => (
  <nav style={{ borderBottom: `1px solid ${HAIR}`, padding: '14px 22px' }}>
    <div style={{
      maxWidth: 960, margin: '0 auto',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    }}>
      {onBack ? (
        <button
          onClick={onBack}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: INK_MUTED, fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 13, letterSpacing: '0.04em', padding: 0,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = INK; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = INK_MUTED; }}
        >
          <ArrowLeft size={14} weight="bold" /> Back
        </button>
      ) : <EventDropNav onClick={() => {}} />}
      {children}
    </div>
  </nav>
);

const StepDots = ({ current }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} aria-label="Step progress">
    {[1, 2, 3].map(s => (
      <div
        key={s}
        style={{
          width: 26, height: 2,
          background: current >= s ? GOLD : 'rgba(212,170,82,0.18)',
          borderRadius: 2, transition: 'background 320ms ease',
        }}
      />
    ))}
  </div>
);

const SectionLabel = ({ children, center }) => (
  <div style={{
    fontSize: 11, fontWeight: 800, letterSpacing: '0.32em',
    textTransform: 'uppercase', color: GOLD, marginBottom: 14,
    textAlign: center ? 'center' : 'left',
  }}>{children}</div>
);

const SectionHeading = ({ children, center }) => (
  <h1 style={{
    fontFamily: SERIF, fontWeight: 500,
    letterSpacing: '-0.01em',
    fontSize: 'clamp(26px, 5vw, 38px)', lineHeight: 1.08,
    margin: 0, color: INK,
    textAlign: center ? 'center' : 'left',
  }}>{children}</h1>
);

const SectionLede = ({ children, center }) => (
  <p style={{
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 'clamp(14px, 1.3vw, 16px)',
    color: INK_MUTED, lineHeight: 1.6,
    margin: '12px 0 26px', maxWidth: 480,
    textAlign: center ? 'center' : 'left',
    marginLeft: center ? 'auto' : 0,
    marginRight: center ? 'auto' : 0,
  }}>{children}</p>
);

const inputBase = {
  width: '100%', padding: '13px 16px',
  borderRadius: 12,
  background: 'rgba(20,12,6,0.78)',
  border: `1px solid ${HAIR}`,
  color: INK, fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 15, outline: 'none',
  boxShadow: 'inset 0 1px 0 rgba(212,170,82,0.08)',
};

const Field = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block', fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 11.5, letterSpacing: '0.16em', textTransform: 'uppercase',
      color: INK_FAINT, marginBottom: 7,
    }}>{label}</label>
    <div
      onFocus={(e) => { const i = e.currentTarget.querySelector('input'); if (i) i.style.borderColor = GOLD; }}
      onBlur={(e) => { const i = e.currentTarget.querySelector('input'); if (i) i.style.borderColor = HAIR; }}
    >
      {children}
    </div>
  </div>
);

const ctaButtonStyle = (active = true, extra = {}) => ({
  padding: '15px 26px',
  borderRadius: 12,
  background: active
    ? 'linear-gradient(135deg, #D4AF5E 0%, #B8923F 100%)'
    : 'rgba(212,170,82,0.18)',
  color: active ? '#0F0B1A' : 'rgba(255,235,210,0.45)',
  border: 'none',
  fontFamily: SERIF, fontWeight: 700, fontSize: 14.5,
  letterSpacing: '0.02em',
  cursor: active ? 'pointer' : 'not-allowed',
  boxShadow: active ? '0 10px 30px rgba(0,0,0,0.35)' : 'none',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
  transition: 'transform 240ms ease',
  ...extra,
});

const Trust = ({ icon, text }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 11.5, color: INK_FAINT, letterSpacing: '0.04em',
  }}>
    <span style={{ color: GOLD, display: 'inline-flex' }}>{icon}</span> {text}
  </span>
);

export default EventCheckout;
